import { NetworkRequestConfig } from '@packages/dataFetch/types';
import { isUndefined } from '../../../predicates/type';
import isEqual from 'lodash/isEqual';
import { AbortRequestByCancelId, AddAbortControllerToMemory, NetworkAbortSource } from './types';
import { emitShowOutdatedAppModal, isOutdatedAppError } from '../../unhandledRejection/outdatedApp/utils';

declare global {
	interface Window {
		__REACT__AXIOS_SOURCES__: Array<NetworkAbortSource>;
	}
}

declare global {
	interface Window {
		__REACT__PENDING_AXIOS_REQUEST_CONFIGS__: Array<NetworkRequestConfig>;
	}
}

export const getDefaultHeaders = () => ({
	SessionGuid: window.__sessionGuid__,
	f: window.__requestVerificationToken__,
	'X-Requested-With': 'XMLHttpRequest',
	'content-type': 'application/json',
});

export const getDefaultGETHeaders = () => ({
	...getDefaultHeaders(),
	Pragma: 'no-cache',
	'Cache-Control': 'no-cache, no-store, must-revalidate',
});

export const getNewAbortController = (): typeof AbortController.prototype | undefined => {
	if ('AbortController' in window) {
		return new AbortController();
	}
};

export const addAbortControllerToMemory: AddAbortControllerToMemory = (abortController, cancelId) => {
	window.__REACT__AXIOS_SOURCES__ = [
		...window.__REACT__AXIOS_SOURCES__,
		{
			abortController,
			path: window.location.pathname,
			search: window.location.search,
			cancelId,
		},
	];
};

export const abortRequestByCancelId: AbortRequestByCancelId = (cancelId) => {
	window.__REACT__AXIOS_SOURCES__ = window.__REACT__AXIOS_SOURCES__.filter((i) => {
		if (i.cancelId === cancelId && typeof i.abortController !== 'undefined') {
			i.abortController.abort();

			return false;
		}

		return true;
	});
};

export function initNetworkGlobals() {
	window.__REACT__AXIOS_SOURCES__ = [];
	window.__REACT__PENDING_AXIOS_REQUEST_CONFIGS__ = [];
}

const findEqualRequestSignal = (url: NetworkRequestConfig['url'], params: NetworkRequestConfig['params']) => {
	return window.__REACT__PENDING_AXIOS_REQUEST_CONFIGS__.find((config) => {
		const normalizedParams = isUndefined(config.params) ? {} : config.params;

		return config.url === url && isEqual(normalizedParams, params);
	})?.signal;
};

export const removePendingRequestConfig = (configToRemove: NetworkRequestConfig) => {
	window.__REACT__PENDING_AXIOS_REQUEST_CONFIGS__ = window.__REACT__PENDING_AXIOS_REQUEST_CONFIGS__.filter(
		(config) => config.signal !== configToRemove.signal,
	);
};

export const removeCanceledRequestConfig = () => {
	const cancelledRequestConfig = window.__REACT__PENDING_AXIOS_REQUEST_CONFIGS__.find(
		(config) => config.signal?.aborted,
	);

	cancelledRequestConfig && removePendingRequestConfig(cancelledRequestConfig);
};

/**
 * Cancels a request with the same url and params
 * */
export const tryToCancelEqualRequest = (url: NetworkRequestConfig['url'], params: NetworkRequestConfig['params']) => {
	const equalRequestSignal = findEqualRequestSignal(url, params);

	if (isUndefined(equalRequestSignal)) {
		return;
	}

	const equalSource = window.__REACT__AXIOS_SOURCES__.find((i) => i.abortController?.signal === equalRequestSignal);

	if (isUndefined(equalSource)) {
		return;
	}

	equalSource.abortController?.abort();
	removeCanceledRequestConfig();
};

export const initUnhandledRejectionHandler = () => {
	window.addEventListener('unhandledrejection', (event) => {
		if (isOutdatedAppError(event as typeof PromiseRejectionEvent.prototype)) {
			emitShowOutdatedAppModal();
		}
	});
};
